import { useState, useCallback, useRef, useEffect } from 'react'
import { config as runtimeConfig } from '../config.js'

/**
 * useVoiceClient — puerto a JSX/Vite de `portal-nextjs/src/hooks/use-voice-client.ts`.
 *
 * Todo el cuerpo real (WebSocket, captura de micrófono, reproducción gapless) es API de
 * browser estándar sin dependencia de Next.js — se portó casi 1:1, solo quitando tipos TS y
 * reemplazando `voiceClientService.getVoiceWebSocketUrl` (que vive en el otro stack) por la
 * misma construcción de URL usando `runtimeConfig.apiUrl` de este portal.
 *
 * Misma API pública que el original: {state, error, transcriptions, toolExecutions, isMuted,
 * callDuration, startCall, endCall, toggleMute} — así el componente de UI (VoicePanelMigracion)
 * no necesita saber que viene de un puerto distinto.
 */

const generateId = () => `vt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

const normalizeError = (error) => {
  if (!error) return 'Error desconocido'
  if (typeof error === 'string') return error
  if (typeof error === 'object') {
    if (typeof error.message === 'string') return error.message
    if (typeof error.error === 'string') return error.error
    try {
      return JSON.stringify(error)
    } catch {
      return 'Error desconocido'
    }
  }
  return String(error)
}

function getVoiceWebSocketUrl(jwtToken) {
  const wsProtocol = typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const apiHost = new URL(runtimeConfig.apiUrl).host
  return `${wsProtocol}//${apiHost}/ws/voice?token=${jwtToken}`
}

/** Reproducción gapless de baja latencia — idéntico al AudioPlayer del hook original. */
class AudioPlayer {
  constructor(onPlayingChange) {
    this.audioContext = null
    this.gainNode = null
    this.nextStartTime = 0
    this.activeSources = new Set()
    this.onPlayingChange = onPlayingChange
    this.isPlaying = false
    this.isStopped = false
  }

  async initialize() {
    if (this.audioContext) return
    this.audioContext = new AudioContext({ latencyHint: 'interactive', sampleRate: 24000 })
    this.gainNode = this.audioContext.createGain()
    this.gainNode.connect(this.audioContext.destination)
    this.gainNode.gain.value = 1.0
    this.nextStartTime = this.audioContext.currentTime
    this.isStopped = false
    if (this.audioContext.state === 'suspended') await this.audioContext.resume()
  }

  async queueAudio(base64Data, sampleRate = 24000) {
    if (this.isStopped) return
    if (!this.audioContext) await this.initialize()
    if (!this.audioContext || !this.gainNode || this.isStopped) return
    if (this.audioContext.state === 'suspended') await this.audioContext.resume()

    try {
      const binaryString = atob(base64Data)
      let pcmData = new Uint8Array(binaryString.length)
      for (let i = 0; i < binaryString.length; i++) pcmData[i] = binaryString.charCodeAt(i)
      if (pcmData.byteLength % 2 !== 0) pcmData = pcmData.subarray(0, pcmData.byteLength - 1)

      const int16Data = new Int16Array(pcmData.buffer, pcmData.byteOffset, pcmData.byteLength / 2)
      const audioBuffer = this.audioContext.createBuffer(1, int16Data.length, sampleRate)
      const channelData = audioBuffer.getChannelData(0)
      for (let i = 0; i < int16Data.length; i++) channelData[i] = int16Data[i] / 32768.0

      const source = this.audioContext.createBufferSource()
      source.buffer = audioBuffer
      source.connect(this.gainNode)

      const currentTime = this.audioContext.currentTime
      const MIN_BUFFER = 0.02
      this.nextStartTime = Math.max(this.nextStartTime, currentTime + MIN_BUFFER)
      source.start(this.nextStartTime)
      this.nextStartTime += audioBuffer.duration

      this.activeSources.add(source)
      this._updatePlayingState(true)
      source.onended = () => {
        this.activeSources.delete(source)
        if (this.activeSources.size === 0) this._updatePlayingState(false)
      }
    } catch (error) {
      console.error('[AudioPlayer] Error queueing audio:', error)
    }
  }

  get playing() {
    return this.isPlaying
  }

  get pendingSources() {
    return this.activeSources.size
  }

  _updatePlayingState(playing) {
    if (this.isPlaying !== playing) {
      this.isPlaying = playing
      this.onPlayingChange?.(playing)
    }
  }

  stop() {
    this.isStopped = true
    this.activeSources.forEach((source) => {
      try {
        source.stop()
        source.disconnect()
      } catch {
        /* ignore */
      }
    })
    this.activeSources.clear()
    this.nextStartTime = 0
    this._updatePlayingState(false)
  }

  async dispose() {
    this.stop()
    if (this.gainNode) {
      this.gainNode.disconnect()
      this.gainNode = null
    }
    if (this.audioContext) {
      try {
        if (this.audioContext.state !== 'closed') await this.audioContext.close()
      } catch {
        /* ignore */
      }
      this.audioContext = null
    }
  }
}

/**
 * @param {{token: string|null, agentConfig?: object|null}} options
 */
export function useVoiceClient({ token, agentConfig }) {
  const [state, setState] = useState('disconnected')
  const [error, setError] = useState(null)
  const [transcriptions, setTranscriptions] = useState([])
  const [toolExecutions, setToolExecutions] = useState([])
  const [isMuted, setIsMuted] = useState(false)
  const [callDuration, setCallDuration] = useState(0)

  const wsRef = useRef(null)
  const audioPlayerRef = useRef(null)
  const recordingContextRef = useRef(null)
  const mediaStreamRef = useRef(null)
  const callStartTimeRef = useRef(0)
  const durationIntervalRef = useRef(null)
  const isMutedRef = useRef(false)
  const sessionEndedRef = useRef(false)

  useEffect(() => {
    isMutedRef.current = isMuted
  }, [isMuted])

  const startDurationTimer = useCallback(() => {
    callStartTimeRef.current = Date.now()
    setCallDuration(0)
    durationIntervalRef.current = setInterval(() => {
      setCallDuration(Math.floor((Date.now() - callStartTimeRef.current) / 1000))
    }, 1000)
  }, [])

  const stopDurationTimer = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current)
      durationIntervalRef.current = null
    }
  }, [])

  const handleTranscription = useCallback((message) => {
    if (!message.text || !message.text.trim()) return
    setTranscriptions((prev) => {
      const last = prev[prev.length - 1]
      if (last && last.role === message.role && !last.isFinal) {
        const updated = [...prev]
        updated[updated.length - 1] = {
          ...last,
          text: message.isFinal ? message.text : last.text + ' ' + message.text,
          isFinal: message.isFinal,
        }
        return updated
      }
      return [...prev, { id: generateId(), role: message.role, text: message.text, timestamp: new Date(), isFinal: message.isFinal }]
    })
  }, [])

  const setupAudioStream = useCallback((ws, stream) => {
    mediaStreamRef.current = stream
    const audioContext = new AudioContext({ sampleRate: 16000, latencyHint: 'interactive' })
    recordingContextRef.current = audioContext

    const source = audioContext.createMediaStreamSource(stream)
    const processor = audioContext.createScriptProcessor(2048, 1, 1)
    source.connect(processor)
    processor.connect(audioContext.destination)

    processor.onaudioprocess = (e) => {
      if (ws.readyState === WebSocket.OPEN && !isMutedRef.current) {
        const inputData = e.inputBuffer.getChannelData(0)
        const int16Data = new Int16Array(inputData.length)
        for (let i = 0; i < inputData.length; i++) {
          int16Data[i] = Math.max(-32768, Math.min(32767, Math.floor(inputData[i] * 32768)))
        }
        const base64 = btoa(String.fromCharCode(...new Uint8Array(int16Data.buffer)))
        ws.send(JSON.stringify({ type: 'audio', data: base64, mimeType: 'audio/pcm;rate=16000' }))
      }
    }
  }, [])

  const cleanupResources = useCallback(() => {
    if (wsRef.current) {
      try {
        wsRef.current.close()
      } catch {
        /* ignore */
      }
      wsRef.current = null
    }
    if (audioPlayerRef.current) {
      audioPlayerRef.current.dispose()
      audioPlayerRef.current = null
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop())
      mediaStreamRef.current = null
    }
    if (recordingContextRef.current) {
      try {
        recordingContextRef.current.close()
      } catch {
        /* ignore */
      }
      recordingContextRef.current = null
    }
    stopDurationTimer()
  }, [stopDurationTimer])

  useEffect(() => {
    return () => cleanupResources()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const startCall = useCallback(async () => {
    if (!token) {
      setError('Token de autenticación no disponible')
      setState('error')
      return
    }
    if (wsRef.current) {
      console.warn('[useVoiceClient] Call already in progress')
      return
    }

    setState('connecting')
    setError(null)
    setTranscriptions([])
    setToolExecutions([])
    setCallDuration(0)
    sessionEndedRef.current = false

    try {
      audioPlayerRef.current = new AudioPlayer((isPlaying) => {
        if (!isPlaying) {
          if (sessionEndedRef.current) return
          if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) setState('disconnected')
          else setState('listening')
        }
      })
      await audioPlayerRef.current.initialize()

      const wsUrl = getVoiceWebSocketUrl(token)
      const ws = new WebSocket(wsUrl)

      ws.onopen = () => {
        console.log('[useVoiceClient] WebSocket connected, waiting for server confirmation...')
      }

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data)
          switch (data.type) {
            case 'connected':
              ws.send(JSON.stringify({ type: 'start_session', agentConfig: agentConfig || {} }))
              break

            case 'session_started':
              setState('connected')
              startDurationTimer()
              navigator.mediaDevices
                .getUserMedia({ audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } })
                .then((stream) => {
                  setupAudioStream(ws, stream)
                  setState('listening')
                })
                .catch((err) => {
                  console.error('[useVoiceClient] Microphone error:', err)
                  setError('No se pudo acceder al micrófono')
                  setState('error')
                })
              break

            case 'audio':
              if (data.data && audioPlayerRef.current) {
                audioPlayerRef.current.queueAudio(data.data, 24000)
                setState('speaking')
              }
              break

            case 'transcription':
              handleTranscription({ role: data.role || 'user', text: data.text || '', isFinal: data.isFinal ?? true })
              break

            case 'tool_call':
              setToolExecutions((prev) => [...prev, { name: data.name || 'unknown', args: data.args, timestamp: new Date() }])
              break

            case 'tool_result':
              setToolExecutions((prev) => {
                const updated = [...prev]
                for (let i = updated.length - 1; i >= 0; i--) {
                  if (updated[i].name === data.name && updated[i].result === undefined) {
                    updated[i] = { ...updated[i], result: data.result }
                    break
                  }
                }
                return updated
              })
              break

            case 'session_ended':
              sessionEndedRef.current = true
              stopDurationTimer()
              setState('disconnected')
              setError(null)
              break

            case 'error':
              setError(normalizeError(data.error) || 'Error en la sesión de voz')
              if (data.code === 'AUTH_FAILED' || data.code === 'TOKEN_EXPIRED') setState('error')
              break

            default:
              break
          }
        } catch (e) {
          console.error('[useVoiceClient] Error parsing WS message:', e)
        }
      }

      ws.onerror = (event) => {
        console.error('[useVoiceClient] WebSocket error:', event)
        setError('Error en la conexión de voz')
        setState('error')
      }

      ws.onclose = (event) => {
        const playerPlaying = audioPlayerRef.current?.playing ?? false
        const pendingSources = audioPlayerRef.current?.pendingSources ?? 0

        if (event.code === 1000 && event.reason === 'session_ended') return

        if (!(playerPlaying || pendingSources > 0)) {
          setState('disconnected')
          if (event.code !== 1000) setError('La conexión se perdió. Intenta de nuevo.')
        }
        stopDurationTimer()
      }

      wsRef.current = ws
    } catch (err) {
      console.error('[useVoiceClient] Error starting call:', err)
      setError('Error iniciando sesión de voz')
      setState('error')
    }
  }, [token, agentConfig, startDurationTimer, stopDurationTimer, setupAudioStream, handleTranscription])

  const endCall = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify({ type: 'end_session' }))
      } catch {
        /* ignore */
      }
    }
    cleanupResources()
    setState('disconnected')
  }, [cleanupResources])

  const toggleMute = useCallback(() => setIsMuted((prev) => !prev), [])

  return { state, error, transcriptions, toolExecutions, isMuted, callDuration, startCall, endCall, toggleMute }
}

export default useVoiceClient
